import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { useGetResponsavelByPessoaIdQuery } from '@/graphql/responsavel/queries.generated';
import type {
  ResponsaveisOnAlunosFieldsFragment,
} from '@/graphql/responsavel/fragments.generated';

/**
 * Tipo para representar um aluno vinculado ao responsável
 * Baseado no fragment ResponsaveisOnAlunosFields
 */
export type AlunoVinculado = ResponsaveisOnAlunosFieldsFragment;

interface ResponsavelAlunoContextData {
  alunos: AlunoVinculado[];
  alunoSelecionado: AlunoVinculado | null;
  isLoading: boolean;
  error: Error | null;
  selecionarAluno: (alunoId: number) => void;
  limparSelecao: () => void;
  refreshAlunos: () => void;
}

const ResponsavelAlunoContext = createContext<ResponsavelAlunoContextData | undefined>(undefined);

interface ResponsavelAlunoProviderProps {
  children: React.ReactNode;
}

const STORAGE_KEY = 'responsavel_aluno_selecionado';

/**
 * Provider que gerencia o estado dos alunos vinculados ao responsável
 * e o aluno selecionado atualmente
 */
export function ResponsavelAlunoProvider({ children }: ResponsavelAlunoProviderProps) {
  const { user } = useAuth();
  const [alunoSelecionadoId, setAlunoSelecionadoId] = useState<number | null>(null);

  // Obter pessoaId do usuário logado
  // IMPORTANTE: User tem pessoa como objeto, então pessoaId é user.pessoa.id
  const pessoaId = useMemo(() => {
    return user?.pessoa?.id ? Number(user.pessoa.id) : null;
  }, [user?.pessoa?.id]);

  // Query para buscar responsável e seus alunos
  const skipQuery = !pessoaId;
  
  const { data, loading, error, refetch } = useGetResponsavelByPessoaIdQuery({
    variables: {
      pessoaId: pessoaId!,
      pagination: { page: 1, limit: 100 },
    },
    skip: skipQuery,
    fetchPolicy: 'cache-and-network',
  });

  // Extrair lista de alunos dos dados retornados
  const alunos = useMemo((): AlunoVinculado[] => {
    if (!data?.responsaveis?.data || data.responsaveis.data.length === 0) {
      return [];
    }

    // Geralmente haverá apenas um responsável por pessoaId
    const responsavel = data.responsaveis.data[0];
    
    if (!responsavel?.filhos) {
      return [];
    }

    // Filtrar apenas vínculos ativos - o tipo já está correto
    return responsavel.filhos.filter((filho) => filho.ativo !== false) as AlunoVinculado[];
  }, [data]);

  // Carregar aluno selecionado do localStorage na inicialização
  useEffect(() => {
    if (alunos.length > 0 && !alunoSelecionadoId) {
      const storedId = localStorage.getItem(STORAGE_KEY);
      if (storedId) {
        const id = parseInt(storedId, 10);
        // Verificar se o ID armazenado ainda existe na lista de alunos
        if (alunos.some((a) => a.alunoId === id)) {
          setAlunoSelecionadoId(id);
          return;
        }
      }
      // Se não há seleção armazenada ou o ID não é mais válido, selecionar o primeiro aluno
      if (alunos.length === 1) {
        setAlunoSelecionadoId(alunos[0].alunoId);
      }
    }
  }, [alunos, alunoSelecionadoId]);

  // Obter aluno selecionado
  const alunoSelecionado = useMemo((): AlunoVinculado | null => {
    if (!alunoSelecionadoId) return null;
    return alunos.find((a) => a.alunoId === alunoSelecionadoId) || null;
  }, [alunos, alunoSelecionadoId]);

  // Persistir seleção no localStorage
  useEffect(() => {
    if (alunoSelecionadoId) {
      localStorage.setItem(STORAGE_KEY, alunoSelecionadoId.toString());
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [alunoSelecionadoId]);

  // Função para selecionar aluno
  const selecionarAluno = useCallback((alunoId: number) => {
    // Validar se o aluno existe na lista
    if (alunos.some((a) => a.alunoId === alunoId)) {
      setAlunoSelecionadoId(alunoId);
    } else {
      console.warn(`Aluno ${alunoId} não encontrado na lista de alunos vinculados`);
    }
  }, [alunos]);

  // Função para limpar seleção
  const limparSelecao = useCallback(() => {
    setAlunoSelecionadoId(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  // Função para atualizar lista de alunos
  const refreshAlunos = useCallback(() => {
    refetch();
  }, [refetch]);

  // Converter erro do Apollo para Error
  const errorState = useMemo(() => {
    if (error) {
      return new Error(error.message || 'Erro ao carregar alunos do responsável');
    }
    return null;
  }, [error]);

  const value: ResponsavelAlunoContextData = {
    alunos,
    alunoSelecionado,
    isLoading: loading,
    error: errorState,
    selecionarAluno,
    limparSelecao,
    refreshAlunos,
  };

  return (
    <ResponsavelAlunoContext.Provider value={value}>
      {children}
    </ResponsavelAlunoContext.Provider>
  );
}

/**
 * Hook para acessar o contexto de alunos do responsável
 */
export function useResponsavelAlunoContext(): ResponsavelAlunoContextData {
  const context = useContext(ResponsavelAlunoContext);
  if (context === undefined) {
    throw new Error('useResponsavelAlunoContext deve ser usado dentro de ResponsavelAlunoProvider');
  }
  return context;
}
